/**
 * Ist die Bildschirmtastatur offen? (30.09.2026, Ticket 2873)
 *
 * Martin, iPhone: *„Das Scrollen … springt unkontrolliert nach unten oder
 * nach oben … und überall ist das Scrollen fehlerhaft."*
 *
 * `body.keyboard-open` blendet Fußzeile und Statusleiste aus und setzt die
 * Höhe von #app auf die sichtbare Höhe (style.css). Gesetzt wurde die Klasse
 * allein aus einer Rechnung: `innerHeight − visualViewport.height − offsetTop
 * > 80`. Auf dem iPhone melden aber beide Zahlen nicht im selben Augenblick:
 * `visualViewport` ist sofort neu, `innerHeight` zieht nach — beim Ein- und
 * Ausfahren der Safari-Leiste, beim Gummiband am Rand und beim Drehen. Für
 * einen Augenblick „fehlen" dann mehr als 80 Punkte, die Klasse springt an,
 * die Fußzeile verschwindet, die Ansicht wird um ihre Höhe länger — und
 * springt wieder zurück, sobald `innerHeight` nachgezogen hat. Wer hin- und
 * herscrollt, löst genau das im Wechsel aus.
 *
 * Eine Tastatur kann aber nicht offen sein, ohne dass ein Feld den Fokus hat,
 * in das man tippt. Das ist die zweite Bedingung: ohne Feld gibt es keine
 * Tastatur, egal was die Rechnung sagt.
 */
import { nachRuhe, schreibeScrollTop } from "./scroll-ruhe.js";

/** Felder, in die man tippt (nicht: Schalter, Regler, Knöpfe, Auswahlkästchen). */
const TIPPFELD =
  'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="file"]):not([type="color"]):not([type="image"]), textarea, select, [contenteditable=""], [contenteditable="true"], iframe';

/** Hat dieses Element den Fokus eines Feldes, das eine Tastatur ruft? */
export function istTippfeld(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  try {
    return el.matches(TIPPFELD);
  } catch {
    return false;
  }
}

/** Ab so vielen fehlenden Punkten gilt die Tastatur als offen (wie bisher). */
export const TASTATUR_AB_PX = 80;

/** Was vom Layout zu sehen ist (Punkte). */
interface SichtLage {
  /** Höhe des Layouts: die größere von `innerHeight` und der Wurzel. */
  layout: number;
  /** Nicht zu sehen: oben weggeschoben plus unten unter der Tastatur (0 bei Zoom). */
  verdeckt: number;
  /** Versatz des sichtbaren Ausschnitts, begrenzt auf [0, layout − sichtbare Höhe]. */
  oben: number;
  /** Unten unter der Tastatur (`--kb`). */
  unten: number;
}

/**
 * Wo der sichtbare Ausschnitt im Layout liegt. (07.10.2026)
 *
 * Martin, iPhone, mit Foto: *„Wenn die Tastatur [offen] ist, passt der Headerbereich nicht mehr.“*
 * Sein Gerät meldete dabei (Journal, `[scroll-t]`, viermal gleich):
 * `inner=451 vv=451 vvTop=401 seite=401`, und die Seite selbst `sicht=852`. Dieses iOS liefert
 * `innerHeight` also GLEICH der sichtbaren Höhe und schiebt den Ausschnitt um 401 nach unten.
 * Gerechnet wurde `innerHeight − vv.height − offsetTop` = −401: keine Tastatur, obwohl 401 Punkte
 * fehlten. `innerHeight` taugt dort nicht als Höhe des Layouts; die Wurzel (`clientHeight`) bleibt 852.
 *
 * ⚠️ Gezoomt (Maßstab > 1) fehlt auch Höhe — das ist keine Tastatur, `verdeckt` bleibt 0.
 * ⚠️ Der Versatz wird begrenzt: Beim Schließen meldete das Gerät schon die volle Höhe, aber noch
 *    `vvTop=459` (Journal) — ein Ausschnitt unterhalb des Layouts.
 *
 * @param innen     `window.innerHeight`
 * @param wurzel    `document.documentElement.clientHeight`
 * @param sicht     `visualViewport.height`
 * @param versatz   `visualViewport.offsetTop`
 * @param massstab  `visualViewport.scale`
 */
export function sichtLage(innen: number, wurzel: number, sicht: number, versatz: number, massstab: number): SichtLage {
  const layout = Math.max(innen, wurzel);
  const verdeckt = massstab > 1.01 ? 0 : Math.max(0, layout - sicht);
  const oben = Math.min(Math.max(0, versatz), Math.max(0, layout - sicht));
  return { layout, verdeckt, oben, unten: Math.max(0, verdeckt - oben) };
}

/**
 * Tastatur offen = es fehlt Höhe UND ein Feld hat den Fokus.
 *
 * @param fehlt  verdeckte Höhe (`sichtLage(…).verdeckt`), nie negativ
 * @param aktiv  `document.activeElement`
 */
export function tastaturOffen(fehlt: number, aktiv: Element | null): boolean {
  return fehlt > TASTATUR_AB_PX && istTippfeld(aktiv);
}

/* ------------------------------------------------------------------ *
 * Das Feld mit dem Fokus bleibt im Bild (07.10.2026, Prüfbefund)
 * ------------------------------------------------------------------ */

/*
 * iOS zeigt ein Feld, indem es den Ausschnitt schiebt (Versatz). Seit #app und die Vollseiten bei offener
 * Tastatur genau im Ausschnitt stehen (style.css, `body.keyboard-open #app`), hebt das diesen Schub auf:
 * Ein Feld weiter unten — Zuhause „Absicht", Einstellungen „Persönlichkeit", ein tiefes Feld in einer
 * Vollseite — landete gemessen genau um den Versatz UNTER dem Sichtbaren (Fenster 188–696, Feld 828–868).
 * Nur der Verfasser im Chat blieb sichtbar, weil er am Boden von #app steht.
 *
 * Darum holt diese Stelle das Feld in SEINER Fläche ins Bild: nach dem Zeichnen, nur die nächste Fläche
 * (nie das Dokument, nie #app, nie der Chat-Verlauf — der folgt chat-folgen), so wenig wie möglich, und
 * über lib/scroll-ruhe.ts (die EINE Regel fürs Scrollen): nicht in eine Bewegung des Lesers hinein,
 * sondern danach.
 */

/** So viel Luft bleibt zwischen Feld und Rand des Sichtbaren (Punkte). */
export const FELD_RAND_PX = 12;

/**
 * Um wie viel muss eine Fläche scrollen, damit das Feld [feldOben, feldUnten] im Fenster [oben, unten]
 * liegt (mit `rand`)? Positiv = nach unten, 0 = es liegt schon darin. So wenig wie möglich; ist das Feld
 * höher als das Fenster, zählt seine Oberkante. Reine Rechnung.
 */
export function feldVersatz(feldOben: number, feldUnten: number, oben: number, unten: number, rand = FELD_RAND_PX): number {
  const o = oben + rand;
  const u = unten - rand;
  if (feldOben < o) return feldOben - o;
  if (feldUnten > u) return Math.min(feldUnten - u, feldOben - o);
  return 0;
}

/** Die nächste Fläche über `el`, die selbst senkrecht scrollt — nie #app, `body` oder das Dokument. */
function naechsteFlaeche(el: Element): HTMLElement | null {
  for (let p = el.parentElement; p && p.id !== "app" && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const y = getComputedStyle(p).overflowY;
    if ((y === "auto" || y === "scroll" || y === "overlay") && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null;
}

/**
 * Der Teil der Fläche, in dem ein Feld wirklich zu sehen ist: ohne ihren `scroll-padding` und ohne einen
 * Kopf, der oben in ihr klebt (`position: sticky`). Gemessen (Prüfbefund 07.10.): In den Einstellungen klebt
 * „‹ Einstellungen“ oben in der Fläche (61–129); ein Feld, das nur bis zur Oberkante der Fläche geholt wurde,
 * lag darunter (340–386, zuoberst über seiner Mitte: der Titel).
 */
function flaechenFenster(flaeche: HTMLElement): { oben: number; unten: number } {
  const k = flaeche.getBoundingClientRect();
  const cs = getComputedStyle(flaeche);
  let oben = k.top + (parseFloat(cs.scrollPaddingTop) || 0);
  const unten = k.bottom - (parseFloat(cs.scrollPaddingBottom) || 0);
  for (const e of document.elementsFromPoint(k.left + k.width / 2, k.top + 1)) {
    if (e === flaeche || !flaeche.contains(e)) continue;
    if (getComputedStyle(e).position === "sticky") oben = Math.max(oben, e.getBoundingClientRect().bottom);
  }
  return { oben, unten };
}

/** Schlüssel des wartenden Auftrags bei lib/scroll-ruhe.ts (der letzte gilt). */
const FELD_AUFTRAG = {};

/**
 * Das Feld in seiner Fläche ins Fenster holen — sofort, oder sobald der Leser die Fläche nicht mehr bewegt.
 * `fenster` liefert das Sichtbare in denselben Koordinaten wie getBoundingClientRect.
 */
function holeFeldInsBild(feld: Element, fenster: () => { oben: number; unten: number }): void {
  const flaeche = naechsteFlaeche(feld);
  if (!flaeche || flaeche.id === "chat") return;
  nachRuhe(
    flaeche,
    FELD_AUFTRAG,
    () => {
      if (document.activeElement !== feld || !flaeche.isConnected) return;
      const sicht = fenster();
      const k = flaechenFenster(flaeche);
      const f = feld.getBoundingClientRect();
      const d = feldVersatz(f.top, f.bottom, Math.max(k.oben, sicht.oben), Math.min(k.unten, sicht.unten));
      if (d !== 0) schreibeScrollTop(flaeche, flaeche.scrollTop + d, "tastatur-feld");
    },
    "tastatur-feld",
  );
}

let letzteHoehe = -1;
let letztesFeld: Element | null = null;
let bild = 0;

/**
 * Soll das Feld geholt werden? Nur wenn es ein anderes Feld ist oder das Fenster um mindestens einen
 * ganzen Punkt KLEINER wurde (gerundet). Reine Rechnung.
 *
 * ⚠️ Nur kleiner, nicht größer und nicht der Versatz: #app und die Vollseiten wandern mit dem Versatz mit,
 *    die Lage des Feldes zum Fenster bleibt dabei gleich — versteckt wird es nur, wenn das Fenster kleiner
 *    wird. Ein reiner Schub kommt auch vom Ziehen am Schirm, ein Wachsen um Bruchteile vom Browser selbst
 *    (Prüfbefund: 508 → 508,33 holte das weggewischte Feld um 300 Punkte zurück). Darauf zu antworten
 *    hieße, den Leser zum Feld zurückzuholen.
 */
export function feldHolen(neuesFeld: boolean, hoehe: number, vorher: number): boolean {
  return neuesFeld || Math.round(hoehe) <= Math.round(vorher) - 1;
}

/**
 * Nach jeder Rechnung des Viewport-Wächters (app.ts): Ist bei offener Tastatur ein anderes Feld dran oder
 * das Fenster kleiner geworden (`feldHolen`), nach dem nächsten Bild das Feld ins Bild holen. Das Fenster
 * ist #app — es steht bei offener Tastatur genau im Sichtbaren.
 */
export function feldImBlick(offen: boolean, hoehe: number): void {
  const feld = document.activeElement;
  if (!offen || !istTippfeld(feld)) {
    letzteHoehe = -1;
    letztesFeld = null;
    return;
  }
  const holen = feldHolen(feld !== letztesFeld, hoehe, letzteHoehe);
  letzteHoehe = hoehe;
  letztesFeld = feld;
  if (!holen) return;
  if (bild) return;
  bild = requestAnimationFrame(() => {
    bild = 0;
    const app = document.getElementById("app");
    const aktiv = document.activeElement;
    if (!app || !aktiv || !document.body.classList.contains("keyboard-open") || !istTippfeld(aktiv)) return;
    holeFeldInsBild(aktiv, () => {
      const r = app.getBoundingClientRect();
      return { oben: r.top, unten: r.bottom };
    });
  });
}
